import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function Admin2FASetupPage() {
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    let active = true;
    api
      .get("/admin/2fa/status")
      .then((r) => {
        if (!active) return;
        setAuthed(true);
        setStatus(r.data.enabled);
      })
      .catch(() => active && setAuthed(false));
    return () => {
      active = false;
    };
  }, []);

  if (authed === false) return <Navigate to="/admin/login" replace />;

  async function start() {
    setBusy(true);
    try {
      const r = await api.post("/admin/2fa/setup");
      setData(r.data);
    } finally {
      setBusy(false);
    }
  }

  async function enable() {
    setBusy(true);
    try {
      await api.post("/admin/2fa/enable", { code });
      toast.success("2FA enabled");
      setStatus(true);
      setData(null);
      setCode("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    if (!window.confirm("Disable 2FA?")) return;
    await api.post("/admin/2fa/disable");
    setStatus(false);
    toast.success("2FA disabled");
  }

  return (
    <main data-testid="admin-2fa-page" className="max-w-xl mx-auto px-6 py-16">
      <h1 className="font-serif-display text-5xl font-bold tracking-tighter">Two-factor auth</h1>
      <p className="mt-3 text-gray-600">Status: <strong>{status ? "enabled" : "disabled"}</strong></p>

      {!status && !data && (
        <Button data-testid="2fa-start" onClick={start} disabled={busy} className="mt-8 rounded-full bg-[#E85D04] hover:bg-[#DC2F02] text-white py-6 px-8">
          Set up 2FA
        </Button>
      )}

      {data && (
        <div className="mt-8 bg-white border border-gray-200 rounded-2xl p-8 space-y-5">
          <p className="text-sm text-gray-600">Scan this QR with 1Password / Authy / Google Authenticator, then enter the 6-digit code:</p>
          <img src={data.qr} alt="QR" data-testid="2fa-qr" className="w-56 h-56 border border-gray-200 rounded-xl" />
          <p className="text-xs font-mono-code text-gray-500 break-all">Manual key: {data.secret}</p>
          <Input data-testid="2fa-confirm-code" inputMode="numeric" maxLength={6} placeholder="123456" value={code} onChange={(e) => setCode(e.target.value)} className="py-6 text-center font-mono-code text-2xl tracking-widest" />
          <Button data-testid="2fa-enable" onClick={enable} disabled={busy || code.length !== 6} className="w-full rounded-full bg-gray-900 hover:bg-gray-800 text-white py-6">
            Enable 2FA
          </Button>
        </div>
      )}

      {status && (
        <Button variant="outline" onClick={disable} className="mt-8 rounded-full">
          Disable 2FA
        </Button>
      )}
    </main>
  );
}
